import { useContext, useEffect, useState } from 'react';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    InscriptionT,
    isOrphan,
    isWandering,
} from 'routes/BudgetModule/shared/entities/inscription';
import { isCustomBudgetProposal } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/budgetProposal';
import { getWanderingInscriptionVentilation } from './dataHelpers';
import { useTranslation } from 'react-i18next';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';

/**
 * A custom hook that manages inscriptions alerts workflow within the AllInscriptions View.
 * Deals with both orphan and wandering inscriptions.
 */
export function useInscriptionAlertsManager(
    inscriptions: InscriptionT[] | undefined | null,
    budgetProposals: BudgetProposalT[],
) {
    const { t } = useTranslation();
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);
    const hasOrphanInscriptions = !!inscriptions?.some((ins) => isOrphan(ins));

    const wanderingInscriptions =
        inscriptions?.filter((ins) => isWandering(ins, budgetProposals)) || [];
    const hasWanderingInscriptions = wanderingInscriptions.length > 0;

    const [showOrphanWarning, setShowOrphanWarning] = useState(false);
    const [showWanderingWarning, setShowWanderingWarning] = useState(false);

    const orphanWarningMessage = isSchool
        ? t('allInscriptions.sifacOrphanInscriptionWarning')
        : t('allInscriptions.orphanInscriptionWarning');

    const wanderingWarningMessage = t(
        'allInscriptions.wanderingInscriptionWarning',
        {
            ventilation: getWanderingInscriptionVentilation(
                wanderingInscriptions || [],
                budgetProposals,
                t,
            ),
        },
    );

    useEffect(() => {
        setShowOrphanWarning(hasOrphanInscriptions);
        setShowWanderingWarning(hasWanderingInscriptions);
    }, [hasOrphanInscriptions, hasWanderingInscriptions]);

    return {
        showOrphanWarning,
        setShowOrphanWarning,
        showWanderingWarning,
        setShowWanderingWarning,
        orphanWarningMessage,
        wanderingWarningMessage,
    };
}
